/* eslint-disable no-undef */
import PropTypes from 'prop-types';
import AbstractButton from './AbstractButton';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import React from 'react';
import { batch } from 'react-redux';
import { ViewIcon } from 'assets/images';
import { createToolbarEvent } from 'store/modules/analytics/AnalyticsEvents';
import { sendAnalytics } from 'store/modules/analytics/functions';
import { setTileView } from 'store/modules/video-layout/actions';
import { shouldDisplayTileView } from 'store/modules/video-layout/functions';
import { setOverflowMenuVisible } from 'store/modules/toolbox/actions';
import { TILE_VIEW_ENABLED } from 'store/modules/base/flags/constants';
import { getFeatureFlag } from 'store/modules/base/flags/functions';
import { translate } from 'store/modules/base/i18n/functions';
import { connect } from 'store/modules/base/redux/functions';
import {
  registerShortcut,
  unregisterShortcut
} from 'store/modules/keyboard-shortcuts/actions';

/**
 * Component that renders a toolbar button for toggling the tile layout view.
 *
 * @augments AbstractButton
 */
class TileViewButton extends AbstractButton {
  accessibilityLabel = 'toolbar.accessibilityLabel.tileView';
  icon = ViewIcon;
  label = 'toolbar.enterTileView';
  toggledLabel = 'toolbar.exitTileView';
  type = 'grey';

  // tooltip = 'toolbar.tileViewToggle';

  /**
   * Initializes a new instance.
   *
   * @inheritdoc
   */
  constructor(props) {
    super(props);

    // Bind event handlers so they are only bound once per instance.
    this._onKeyboardShortcut = this._onKeyboardShortcut.bind(this);
  }

  /**
   * Registers the keyboard shortcut that toggles the audio muting.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidMount() {
    this.props.dispatch(
      registerShortcut({
        character: 'E',
        helpDescription: 'keyboardShortcuts.tileView',
        handler: this._onKeyboardShortcut
      })
    );
  }

  /**
   * Unregisters the keyboard shortcut that toggles the audio muting.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentWillUnmount() {
    this.props.dispatch(unregisterShortcut('E'));
  }

  /**
   * Creates an analytics keyboard shortcut event and dispatches an action to
   * toggle the audio muting.
   *
   * @private
   * @returns {void}
   */
  _onKeyboardShortcut() {
    // Ignore keyboard shortcuts if the audio button is disabled.
    if (this._isDisabled()) {
      return;
    }

    AbstractButton.prototype._onClick.call(this);
  }

  /**
   * Handles clicking / pressing the button.
   *
   * @override
   * @protected
   * @returns {void}
   */
  _handleClick() {
    const { _tileViewEnabled, dispatch } = this.props;

    // console.log(_isAudioActive);

    // if (_isAudioActive) {
    //   return;
    // }
    const value = !_tileViewEnabled;

    sendAnalytics(
      createToolbarEvent('tileview.button', {
        is_enabled: value
      })
    );

    // dispatch(toggleTileView());

    batch(() => {
      dispatch(setTileView(value));
      navigator.product !== 'ReactNative' &&
        dispatch(setOverflowMenuVisible(false));
    });
  }

  /**
   * Indicates whether this button is in toggled state or not.
   *
   * @override
   * @protected
   * @returns {boolean}
   */
  _isToggled() {
    return this.props._tileViewEnabled;
  }
}

/**
 * Maps (parts of) the redux state to the associated props for the
 * {@code TileViewButton} component.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The properties explicitly passed to the component instance.
 * @returns {Props}
 */
function _mapStateToProps(state, ownProps) {
  const enabled = getFeatureFlag(state, TILE_VIEW_ENABLED, true);
  const { visible = enabled } = ownProps;

  return {
    _tileViewEnabled: shouldDisplayTileView(state),
    _isAudioActive: state['features/filmstrip'].isAudioActive,
    _userIdleMode: state['features/settings'].userIdleMode,
    visible
  };
}
/**
 * The type of the React {@code Component} props of {@link TileViewButton}.
 */
TileViewButton.propTypes = {
  /**
   * Whether or not tile view layout has been enabled as the user preference.
   */
  _tileViewEnabled: PropTypes.bool,
  _isAudioActive: PropTypes.bool,
  showLabel: PropTypes.bool,

  /**
   * Used to dispatch actions from the buttons.
   */
  dispatch: PropTypes.func
};
export default React.memo(
  translate(connect(_mapStateToProps)(withBoundary(TileViewButton)))
);
